import {
  Slide,
  SlideImage as SlideImageType,
  TextAlignment,
} from '@/client/courses/types/courses.type';
import { SlideImageComponent } from '@/components/slides/elements/SlideImageComponent';
import { FlexContainer } from '@/ui/styled-ui';
import { filterValidSlideImagesForDisplay } from '@/utils/helpers/courses.helper';
import React from 'react';
import 'react-quill/dist/quill.snow.css';
import styled from 'styled-components';
import { RichTextArea } from './RichTextArea';
import { RichTextTitle } from './RichTextTitle';

const StyledQuillWrapper = styled.div`
  width: 100%;
  margin: 0;
  padding: 0;

  .quill-editor-v2 .ql-editor {
    min-height: auto !important;
    padding-bottom: 0 !important;
  }
  .quill-editor-v2 .ql-container {
    height: auto !important;
  }
  .quill-editor-v2 .ql-editor.ql-blank::before {
    bottom: 0 !important;
  }
`;

const ImageGridContainer = styled.div<{
  textAlignment: TextAlignment;
  isGallery?: boolean;
}>`
  display: grid;
  grid-auto-flow: column;
  grid-template-rows: auto;
  grid-auto-columns: ${({ isGallery }) =>
    isGallery ? 'minmax(0, 1fr)' : '1fr'};
  gap: var(--small-padding);
  width: 100%;
  max-width: ${({ isGallery }) => (isGallery ? '100%' : '375px')};
  min-width: 0;
  box-sizing: border-box;
  align-items: start;
  justify-items: ${({ textAlignment }) =>
    textAlignment === TextAlignment.CENTER
      ? 'center'
      : textAlignment === TextAlignment.RIGHT
      ? 'end'
      : 'start'};
  justify-content: ${({ textAlignment }) =>
    textAlignment === TextAlignment.CENTER
      ? 'center'
      : textAlignment === TextAlignment.RIGHT
      ? 'flex-end'
      : 'flex-start'};
  align-self: ${({ textAlignment }) =>
    textAlignment === TextAlignment.CENTER
      ? 'center'
      : textAlignment === TextAlignment.RIGHT
      ? 'flex-end'
      : 'flex-start'};
  margin: 0;
  padding: 0;
  overflow: visible;

  > * {
    height: auto;
    min-height: 0;
    overflow: visible;
  }
`;

interface GalleryFormProps {
  currentEditingSlide: Slide;
  currentSlideData: Slide;
  selectedLanguage: string;
  isEditingPopUpSlide: boolean;
  currentSlideIdx: number;
  totalSlides: number;
  updateSlide: (id: string, updates: Partial<Slide>) => void;
}

export const GalleryForm: React.FC<GalleryFormProps> = ({
  currentEditingSlide,
  updateSlide,
  selectedLanguage,
  isEditingPopUpSlide,
  currentSlideIdx,
  currentSlideData,
}) => {
  const validImages = filterValidSlideImagesForDisplay(currentSlideData.images);

  return (
    <FlexContainer
      direction="column"
      justify="flex-start"
      align={
        currentSlideData.textAlignment === TextAlignment.CENTER
          ? 'center'
          : currentSlideData.textAlignment === TextAlignment.RIGHT
          ? 'flex-end'
          : 'flex-start'
      }
    >
      <RichTextTitle
        currentEditingSlide={currentEditingSlide}
        isEditingPopUpSlide={isEditingPopUpSlide}
        currentSlideData={currentSlideData}
        updateSlide={updateSlide}
      />
      <StyledQuillWrapper>
        <RichTextArea
          currentEditingSlide={currentEditingSlide}
          isEditingPopUpSlide={isEditingPopUpSlide}
          selectedLanguage={selectedLanguage}
          currentSlideIdx={currentSlideIdx}
          currentSlideData={currentSlideData}
          updateSlide={updateSlide}
        />
      </StyledQuillWrapper>
      {validImages && validImages.length > 0 && (
        <ImageGridContainer
          textAlignment={currentSlideData.textAlignment || TextAlignment.LEFT}
          isGallery={true}
          style={{ marginTop: '16px' }}
        >
          {validImages.map((image: SlideImageType, index: number) => (
            <SlideImageComponent
              key={index}
              image={image}
              currentEditingSlide={currentEditingSlide}
              updateSlide={updateSlide}
              textAlignment={
                currentSlideData.textAlignment || TextAlignment.LEFT
              }
              isGallery={true}
            />
          ))}
        </ImageGridContainer>
      )}
    </FlexContainer>
  );
};
